import _ from 'lodash';
import { ReportRow } from '@/services/ReportingService';
import type { ChartModalConfig } from '@/components/reporting/ChartSeriesModal';

export type AggregatedXAxisKey =
  | 'lineItem'
  | 'siteName'
  | 'areaOfExpertise'
  | 'inspectorName'
  | 'status'
  | 'group'
  | 'inspectedOn';

export type DateInterval = 'day' | 'month' | 'year';

export type AggregatedYMetric =
  | 'inspectionCount'
  | 'inspectionTotal'
  | 'inspectionAverage'
  | 'compositeScore'
  | 'checklistItemCount'
  | 'correctiveActionCount'
  | 'attachmentCount';

interface AggregatedPoint {
  x: string;
  y: number;
}

interface DateBucket {
  label: string;
  start: Date;
  end: Date;
}

const toRecord = (row: ReportRow): Record<string, unknown> => row as unknown as Record<string, unknown>;

const dateKey = (date: Date, interval: DateInterval): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (interval === 'day') {
    return `${year}-${month}-${day}`;
  }

  if (interval === 'month') {
    return `${year}-${month}`;
  }

  return `${year}`;
};

const parseDate = (value: unknown): Date | null => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatXAxisValue = (value: unknown): string => {
  if (value == null) {
    return 'N/A';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : 'N/A';
  }

  return String(value);
};

const sumMetric = (rows: ReportRow[], field: 'compositeScore' | 'checklistItemCount' | 'correctiveActionCount' | 'attachmentCount'): number => {
  return rows.reduce((total, row) => {
    const value = Number(toRecord(row)[field]);
    return Number.isFinite(value) ? total + value : total;
  }, 0);
};

const averageCompositeScore = (rows: ReportRow[]): number => {
  const numeric = rows
    .map(row => Number(toRecord(row).compositeScore))
    .filter(value => Number.isFinite(value));

  if (numeric.length === 0) {
    return 0;
  }

  const total = numeric.reduce((sum, value) => sum + value, 0);
  return total / numeric.length;
};

const aggregateMetricValue = (rows: ReportRow[], metric: AggregatedYMetric): number => {
  switch (metric) {
    case 'inspectionCount':
      return rows.length;
    case 'inspectionTotal':
      return rows.length;
    case 'inspectionAverage':
      return averageCompositeScore(rows);
    case 'compositeScore':
      return sumMetric(rows, 'compositeScore');
    case 'checklistItemCount':
      return sumMetric(rows, 'checklistItemCount');
    case 'correctiveActionCount':
      return sumMetric(rows, 'correctiveActionCount');
    case 'attachmentCount':
      return sumMetric(rows, 'attachmentCount');
    default:
      return 0;
  }
};

const buildDateBuckets = (dates: Date[], interval: DateInterval): DateBucket[] => {
  if (dates.length === 0) {
    return [];
  }

  const sortedDates = _.sortBy(dates, date => date.getTime());
  const minDate = sortedDates[0];
  const maxDate = sortedDates[sortedDates.length - 1];

  const cursor = new Date(minDate);
  if (interval === 'month') {
    cursor.setDate(1);
  }
  if (interval === 'year') {
    cursor.setMonth(0, 1);
  }
  cursor.setHours(0, 0, 0, 0);

  const buckets: DateBucket[] = [];

  while (cursor.getTime() <= maxDate.getTime()) {
    const start = new Date(cursor);
    const end = new Date(cursor);

    if (interval === 'day') {
      end.setDate(end.getDate() + 1);
      cursor.setDate(cursor.getDate() + 1);
    } else if (interval === 'month') {
      end.setMonth(end.getMonth() + 1);
      cursor.setMonth(cursor.getMonth() + 1);
    } else {
      end.setFullYear(end.getFullYear() + 1);
      cursor.setFullYear(cursor.getFullYear() + 1);
    }

    buckets.push({
      label: dateKey(start, interval),
      start,
      end,
    });
  }

  return buckets;
};

const aggregateByDateInterval = (
  selectedRows: ReportRow[],
  yMetric: AggregatedYMetric,
  interval: DateInterval,
): AggregatedPoint[] => {
  const rowsWithDate = selectedRows
    .map(row => ({ row, date: parseDate(toRecord(row).inspectedOn) }))
    .filter((entry): entry is { row: ReportRow; date: Date } => entry.date != null);

  if (rowsWithDate.length === 0) {
    return [];
  }

  const buckets = buildDateBuckets(rowsWithDate.map(entry => entry.date), interval);

  const grouped = _.groupBy(rowsWithDate, entry => dateKey(entry.date, interval));

  return buckets.map(bucket => {
    const rowsInBucket = (grouped[bucket.label] ?? []).map(entry => entry.row);
    return {
      x: bucket.label,
      y: aggregateMetricValue(rowsInBucket, yMetric),
    };
  });
};

const aggregateByField = (
  selectedRows: ReportRow[],
  xColumn: Exclude<AggregatedXAxisKey, 'inspectedOn'>,
  yMetric: AggregatedYMetric,
): AggregatedPoint[] => {
  const grouped = _.groupBy(selectedRows, row => formatXAxisValue(toRecord(row)[xColumn]));

  return _.sortBy(
    Object.keys(grouped).map(groupKey => ({
      x: groupKey,
      y: aggregateMetricValue(grouped[groupKey], yMetric),
    })),
    point => point.x.toLowerCase(),
  );
};

class InspectionAggregatedChartUtil {
  public static buildChartOptions(selectedRows: ReportRow[], config: ChartModalConfig): Record<string, any> {
    const data: AggregatedPoint[] =
      config.xColumn === 'inspectedOn'
        ? aggregateByDateInterval(selectedRows, config.yMetric, config.dateInterval)
        : aggregateByField(selectedRows, config.xColumn, config.yMetric);

    const sortedData = _.sortBy(data, point => point.x);

    const series =
      config.chartType === 'pie'
        ? [{
            type: 'pie',
            angleKey: 'y',
            calloutLabelKey: 'x',
            sectorLabelKey: 'y',
            ...(config.pieDonutRatio && config.pieDonutRatio > 0
              ? { innerRadiusRatio: config.pieDonutRatio }
              : {}),
            ...(config.pieOuterRadiusRatio !== undefined && config.pieOuterRadiusRatio < 1
              ? { outerRadiusRatio: config.pieOuterRadiusRatio }
              : {}),
          }]
        : [{
            type: config.chartType,
            xKey: 'x',
            yKey: 'y',
          }];

    const options: Record<string, any> = {
      data: sortedData,
      series,
      title: {
        text: `${config.yMetricLabel} by ${config.xColumnLabel}`,
      },
    };

    if (config.chartType !== 'pie') {
      options.axes = [
        {
          type: 'category',
          position: 'bottom',
          title: { text: config.xColumnLabel },
        },
        {
          type: 'number',
          position: 'left',
          title: { text: config.yMetricLabel },
        },
      ];
    }

    return options;
  }
}

export default InspectionAggregatedChartUtil;
